import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Users, Pencil, Plus, Search } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { PlayerSearchField, type PlayerSelection } from './PlayerSearchField';
import type { EventItem, EventPlayerTeam } from '../../types/eventTeam';

export interface CheckTeamModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  team?: EventPlayerTeam | null;
  /** Pre-link to an event. */
  presetEventId?: string;
  onClose: () => void;
  onSuccess: (team: EventPlayerTeam) => void;
}

interface DraftMember {
  user_uuid: string;
  display_name: string;
}

/**
 * Clearance player team (guide §3.1): named list of player UUIDs, optionally
 * linked to an event, reusable across checks. Create supports inline members.
 */
export function CheckTeamModal({ isOpen, mode, team, presetEventId, onClose, onSuccess }: CheckTeamModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [notes, setNotes] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const debouncedEventSearch = useDebounce(eventSearch, 300);
  const [eventResults, setEventResults] = useState<EventItem[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [members, setMembers] = useState<DraftMember[]>([]);
  const [newMember, setNewMember] = useState<PlayerSelection | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setEventSearch('');
    setEventResults([]);
    setMembers([]);
    setNewMember(null);
    if (mode === 'edit' && team) {
      setName(team.name);
      setColor(team.color || '#3b82f6');
      setNotes(team.notes ?? '');
      if (team.event_id) {
        eventTeamApiService
          .getEvent(team.event_id)
          .then((e) => setSelectedEvent(e))
          .catch(() => setSelectedEvent(null));
      } else {
        setSelectedEvent(null);
      }
    } else {
      setName('');
      setColor('#3b82f6');
      setNotes('');
      setSelectedEvent(null);
      if (presetEventId) {
        eventTeamApiService
          .getEvent(presetEventId)
          .then((e) => setSelectedEvent(e))
          .catch(() => setSelectedEvent(null));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || debouncedEventSearch.trim().length === 0) {
      setEventResults([]);
      return;
    }
    let cancelled = false;
    eventTeamApiService
      .searchEvents(debouncedEventSearch)
      .then((res) => {
        if (!cancelled) setEventResults(res);
      })
      .catch(() => {
        if (!cancelled) setEventResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedEventSearch, isOpen]);

  const addDraftMember = () => {
    if (!newMember || members.some((m) => m.user_uuid === newMember.uuid)) return;
    setMembers((prev) => [...prev, { user_uuid: newMember.uuid, display_name: newMember.name ?? '' }]);
    setNewMember(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        const created = await eventTeamApiService.createCheckTeam({
          name: name.trim(),
          color: color || null,
          notes: notes.trim() || null,
          event_id: selectedEvent?.id ?? null,
          members:
            members.length > 0
              ? members.map((m) => ({ user_uuid: m.user_uuid, display_name: m.display_name || null }))
              : undefined,
        });
        onSuccess(created);
      } else if (team) {
        const updated = await eventTeamApiService.updateCheckTeam(team.id, {
          name: name.trim(),
          color: color || null,
          notes: notes.trim() || null,
          event_id: selectedEvent?.id ?? null,
        });
        onSuccess(updated);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save team');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          {mode === 'create' ? (
            <>
              <Users className="w-6 h-6 text-accent-400" />
              New player team
            </>
          ) : (
            <>
              <Pencil className="w-6 h-6 text-accent-400" />
              Edit player team
            </>
          )}
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Team name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. Team Blue"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Color</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="glass-input h-[38px] w-16 p-1 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Linked event (optional)</label>
            {selectedEvent ? (
              <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                <span className="text-sm font-medium text-white truncate">{selectedEvent.title}</span>
                <button
                  type="button"
                  onClick={() => setSelectedEvent(null)}
                  className="text-glass-400 hover:text-white flex-shrink-0 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="glass-input w-full pl-10 text-sm"
                  placeholder="Search events…"
                />
                {eventResults.length > 0 && eventSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 glass-card border border-glass-200/20 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto">
                    {eventResults.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => {
                          setSelectedEvent(e);
                          setEventSearch('');
                          setEventResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
                      >
                        {e.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="glass-input w-full text-sm" />
          </div>

          {mode === 'create' && (
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Inline members (optional)</label>
              <div className="flex items-center gap-2 mb-2">
                <PlayerSearchField
                  value={newMember}
                  onChange={setNewMember}
                  className="flex-1"
                  placeholder="Add player by name…"
                />
                <button
                  type="button"
                  onClick={addDraftMember}
                  disabled={!newMember}
                  className="glass-button-secondary !p-2"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {members.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {members.map((m) => (
                    <span
                      key={m.user_uuid}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-glass-200/10 text-xs text-glass-300 font-mono"
                    >
                      {m.display_name || m.user_uuid.slice(0, 8)}
                      <button
                        type="button"
                        onClick={() => setMembers((prev) => prev.filter((x) => x.user_uuid !== m.user_uuid))}
                        className="text-glass-500 hover:text-red-300"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {submitting && <Loader className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Create team' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
